import { useCallback, useEffect, useState } from 'react';

import { boot } from '../../utils/frontchat';
import type { FrontChat, FrontChatOptions, FrontChatParams } from '../../utils/types';

/*
 * Types.
 */

type Initialize = (params?: FrontChatParams) => void;

export interface UseFrontChatBootReturn {
  frontChat: FrontChat | undefined;
  isInitialized: boolean;
  initialize: Initialize | undefined;
}

enum FrontChatStatusesEnum {
  IDLE = 'idle',
  READY = 'ready',
  INITIALIZED = 'initialized',
}

/*
 * Hook.
 */

export const useFrontChatBoot = (element?: HTMLElement, options?: FrontChatOptions): UseFrontChatBootReturn => {
  const [status, setStatus] = useState<FrontChatStatusesEnum>(FrontChatStatusesEnum.IDLE);

  // `boot` shares a single script load across hook instances, so calling it again is cheap.
  const loadScript = useCallback(async () => {
    try {
      await boot(element, options);
      setStatus((current) => (current === FrontChatStatusesEnum.IDLE ? FrontChatStatusesEnum.READY : current));
    } catch (error) {
      console.error(error);
    }
  }, [element, options]);

  useEffect(() => {
    void loadScript();
  }, [loadScript]);

  if (status === FrontChatStatusesEnum.IDLE) {
    return {
      frontChat: undefined,
      isInitialized: false,
      initialize: undefined,
    };
  }

  const frontChat: FrontChat = (cmdType, params) => {
    if (!window?.FrontChat) {
      console.error('[front-chat-sdk] Have not finished setting up window.FrontChat');
      return;
    }

    if (cmdType === 'init') {
      const onInitCompleted = () => {
        setStatus(FrontChatStatusesEnum.INITIALIZED);
      };

      return window?.FrontChat('init', {
        chatId: params?.chatId,
        useDefaultLauncher: true,
        userHash: params?.token,
        email: params?.email,
        name: params?.name,
        customFields: {
          requestId: params?.requestId,
        },
        onInitCompleted,
      });
    }

    if (cmdType === 'shutdown') {
      setStatus(FrontChatStatusesEnum.READY);
    }

    return window.FrontChat(cmdType, params);
  };

  const initialize = (params?: FrontChatParams) => {
    if (status === FrontChatStatusesEnum.INITIALIZED) {
      return;
    }

    frontChat('init', params);
  };

  return {
    frontChat,
    isInitialized: status === FrontChatStatusesEnum.INITIALIZED,
    initialize,
  };
};
